import type { Messages } from "@/i18n/locales/en";
import { legalMessagesHu } from "@/i18n/locales/legalHuData";
import { pagesMessagesHu } from "@/i18n/locales/pagesHuData";

export const huMessages = {
  meta: {
    description:
      "WasOK — termékek beolvasása, értékelések és jegyzetek mentése, emlékezzen arra, ami megérte.",
    title: "WasOK — Emlékezzen arra, ami megérte",
  },
  header: {
    navFeatures: "Funkciók",
    navHowItWorks: "Hogyan működik",
    navPrivacy: "Adatvédelem",
    navUseCases: "Használati esetek",
    download: "Letöltés",
    openMenu: "Menü megnyitása",
    closeMenu: "Menü bezárása",
    languageAria: "Nyelv",
  },
  hero: {
    badge: "📱 Most már Androidon is elérhető",
    title: "Emlékezzen arra, ami megérte.",
    subtitle:
      "Olvasson be termékeket, mentse az értékeléseket és jegyzeteket — és azonnal emlékezzen, hogy egy termék tényleg jó volt-e.",
    googlePlay: "Letöltés a Google Playről",
    watchDemo: "Demó megtekintése",
    privacyLine: "Minden adat az eszközén marad.",
    phoneTitle: "WasOK",
    phoneSubtitle: "Beolvasás az emlékezéshez",
    snackCard: "Finom!",
    coffeeCard: "Túl keserű",
    scrollHint: "Görgessen a felfedezéshez",
  },
  problems: {
    heading: "Ismerős?",
    subheading:
      "Mindannyian voltunk már így — a polcok előtt állva próbáljuk felidézni, hogy tetszett-e legutóbb.",
    notedAgo: "✓ Ezt 3 hónapja jegyezte fel",
    items: [
      {
        question: "Melyik csokoládé ízlett tényleg?",
        note: "Tökéletes édesség",
      },
      {
        question: "Miért hagytam abba ennek a samponnak a vásárlását?",
        note: "Kiszárította a hajam",
      },
      {
        question: "Ez a kávé jó volt vagy csalódás?",
        note: "Túl keserű",
      },
    ],
  },
  howItWorks: {
    heading: "Hogyan működik",
    subheading:
      "Négy egyszerű lépés, hogy soha többé ne felejtse el a termékvéleményeit.",
    stepPrefix: "Lépés",
    steps: [
      {
        title: "Olvasson be bármilyen termékvonalkódot",
        description:
          "Gyors és egyszerű beolvasás a kamerával. Bármilyen vonalkóddal működik.",
      },
      {
        title: "Mentse az értékeléseket, megjegyzéseket és fotókat",
        description:
          "Értékelje a termékeket, adjon hozzá jegyzeteket, csatoljon fotókat, naplózza az árakat és helyeket.",
      },
      {
        title: "Azonnal emlékezzen a tapasztalatára",
        description:
          "Olvassa be ugyanazt a terméket később, és lássa a korábbi értékelését és jegyzeteit.",
      },
      {
        title: "Készítsen okos bevásárlólistákat",
        description:
          "Hozzon létre bevásárlólistákat olyan termékekből, amelyekben már megbízik és amelyeket szeret.",
      },
    ],
  },
  privacy: {
    heading: "A vásárlási előzményei a tiéi maradnak.",
    body: "A WasOK segít emlékezni a kipróbált termékekre, miközben az értékeléseit, jegyzeteit, fotóit és bevásárlólistáit helyben tárolja az eszközén.",
    trustBanner:
      "💚 A vásárlási előzményei köré építve — nem az adatai gyűjtésére.",
    bullets: [
      "Nincs szükség fiókra",
      "Nincs szükség felhőszinkronizálásra",
      "Helyi elsőbbségű tárolás",
      "Felhasználó által vezérelt biztonsági mentések",
      "Opcionális vonalkód-keresés termékinformációkhoz.",
      "Hirdetésalapú a Google AdMob-bal",
    ],
  },
  features: {
    heading: "Minden, amire szüksége van",
    subheading:
      "Erőteljes funkciók, amelyek egyszerűvé és priváttá teszik a termékkövetést.",
    items: [
      {
        title: "Vonalkódolvasó",
        description: "Gyors és pontos vonalkód-beolvasás bármilyen termékhez.",
      },
      {
        title: "Termékértékelések",
        description: "Értékelje a termékeket 1–5 csillaggal a minőség megjegyzéséhez.",
      },
      {
        title: "Termékfotók",
        description: "Csatoljon fotókat a csomagolás vagy a részletek megjegyzéséhez.",
      },
      {
        title: "Kategóriák",
        description: "Szervezze a termékeket egyedi kategóriákba.",
      },
      {
        title: "Üzletkövetés",
        description: "Emlékezzen, hol vásárolta az egyes termékeket.",
      },
      {
        title: "Árnaplózás",
        description: "Kövesse az árakat a legjobb ajánlatok megtalálásához.",
      },
      {
        title: "Bevásárlólisták",
        description: "Készítsen listákat a kedvenc termékeiből.",
      },
      {
        title: "Offline hozzáférés",
        description: "Tökéletesen működik internetkapcsolat nélkül.",
      },
      {
        title: "Helyi tárolás",
        description: "Minden adata biztonságosan tárolódik az eszközén.",
      },
    ],
  },
  useCases: {
    heading: "Tökéletes minden polchoz",
    subheading:
      "Akár élelmiszert, szépségápolást vagy háztartási cikkeket vásárol — a WasOK segít emlékezni.",
    cards: [
      {
        emoji: "🍫",
        title: "Snackek és édességek",
        description: "Emlékezzen, mely cukorkák voltak tényleg jók.",
      },
      {
        emoji: "☕",
        title: "Kávé és italok",
        description: "Kövesse a kávékat, amelyeket érdemes újra megvenni.",
      },
      {
        emoji: "💄",
        title: "Kozmetikumok és bőrápolás",
        description: "Jegyezze fel a bőrreakciókat.",
      },
      {
        emoji: "🧹",
        title: "Háztartási cikkek",
        description: "Kerülje el a kudarcot vallott termékek újravásárlását.",
      },
    ],
  },
  screenshots: {
    heading: "Szép és intuitív",
    subheading:
      "Átgondoltan tervezett felület, amely örömmé teszi a termékkövetést.",
    items: [
      { title: "Olvasó" },
      { title: "Termékrészletek" },
      { title: "Értékelés megadása" },
      { title: "Keresés és előzmények" },
      { title: "Bevásárlólista" },
    ],
  },
  cta: {
    heading: "Bevásárlási memória a valós élethez.",
    body: "Ne felejtse el többé, mely termékek érték meg a vásárlást. Vásároljon okosabban még ma.",
    googlePlay: "Letöltés a Google Playről",
    scanQr: "Beolvasás a letöltéshez",
    tagline: "Privát · Offline-first · Egyszerű",
  },
  footer: {
    tagline: "Emlékezzen arra, ami megérte.",
    linksHeading: "Linkek",
    privacyPolicy: "Adatvédelmi irányelvek",
    terms: "Szolgáltatási feltételek",
    contact: "Kapcsolat",
    linkFaq: "GYIK",
    linkLicenses: "Nyílt forráskódú licencek",
    linkAdvertising: "Hirdetés és monetizáció",
    downloadHeading: "Letöltés",
    googlePlay: "Google Play",
    copyright:
      "© 2026 WasOK. Minden jog fenntartva. 💚-vel készült az okosabb vásárlásért.",
  },
  legal: legalMessagesHu,
  faq: pagesMessagesHu.faq,
  support: pagesMessagesHu.support,
  advertising: pagesMessagesHu.advertising,
} satisfies Messages;
